import { Col, Container, Flex, Row, Shape, styled } from '@teko/ds-desktop';
import { BannerEntity, BannerProps, MegaMenu } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { MegaMenuItemInterface } from '@teko/types/pb';
import { BannerSlides } from 'components/desktop/organisms/BannerSlides';
import dynamic from 'next/dynamic';

const FeaturedBanners = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.FeaturedBanners),
);

const EnhancedImage = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.EnhancedImage),
);

interface NewLayoutAboveTheFoldProps {
  menuItems: Array<MegaMenuItemInterface>;
  slideBanners: Array<BannerEntity>;
  horizontalFourBanners: Array<BannerEntity>;
  topRightBanners: Array<BannerEntity>;
}

const ABOVE_THE_FOLD_MAX_WIDTH = 1232;
const ABOVE_THE_FOLD_HEIGHT = 424;
const MEGA_MENU_COL_WIDTH = (ABOVE_THE_FOLD_MAX_WIDTH - 32) / 6 - 16;
const SLIDE_BANNERS_HEIGHT = 280;

export const NewLayoutAboveTheFold: React.FC<NewLayoutAboveTheFoldProps> = (
  props,
) => {
  const { menuItems, slideBanners, horizontalFourBanners, topRightBanners } =
    props;

  return (
    <Container
      height={ABOVE_THE_FOLD_HEIGHT}
      marginBottom="s24"
      marginTop="s16"
    >
      <Row justify="space-between">
        <StyledMegaMenuCol span={2} paddingRight="s16">
          {!!menuItems?.length ? (
            <MegaMenu menuItems={menuItems} type="vertical" />
          ) : null}
        </StyledMegaMenuCol>
        <Col span={8}>
          <WideThickWrapper
            maxHeight={((ABOVE_THE_FOLD_MAX_WIDTH - 32) / 3) * 2}
          >
            <BannerSlides
              imageWidth={((ABOVE_THE_FOLD_MAX_WIDTH - 32) / 3) * 2}
              imageHeight={SLIDE_BANNERS_HEIGHT}
              banners={slideBanners}
              prioritized={true}
              showControl={true}
              displayNewLayout={true}
            />
          </WideThickWrapper>
          {!!horizontalFourBanners.length && (
            <Row
              marginTop="s12"
              maxHeight={ABOVE_THE_FOLD_HEIGHT - SLIDE_BANNERS_HEIGHT - 16}
            >
              <Col span={12}>
                <FeaturedBanners
                  prioritized={true}
                  regionName="bottomBanner"
                  banners={horizontalFourBanners.map((banner): BannerProps => {
                    return {
                      src: banner.image,
                      link: {
                        href: banner.targetPath,
                      },
                      name: banner.campaignName,
                      lazy: false,
                      height: ABOVE_THE_FOLD_HEIGHT - SLIDE_BANNERS_HEIGHT - 16,
                    };
                  })}
                />
              </Col>
            </Row>
          )}
        </Col>
        <Col span={2} paddingLeft="s16">
          {topRightBanners.length !== 0 && (
            <Flex direction="column" gap="s12">
              {topRightBanners.map((banner, index) => (
                <EnhancedImage
                  key={index}
                  link={{
                    href: banner.targetPath,
                  }}
                  tracking={getTrackingData({
                    isTrackContent: true,
                    regionName: 'topRightBannerHomepage',
                    contentName: banner.campaignName,
                    index: index,
                    target: banner.targetPath,
                  })}
                  src={banner.image}
                  borderRadius={8}
                  height={(ABOVE_THE_FOLD_HEIGHT - 16) / 2}
                  objectFit="cover"
                  googleImageParams={{
                    width: (ABOVE_THE_FOLD_MAX_WIDTH - 32) / 6 - 16,
                  }}
                  width={(ABOVE_THE_FOLD_MAX_WIDTH - 32) / 6 - 16}
                  hover="zoom"
                  alt={banner.campaignName || banner.description || ''}
                  lazy={false}
                  fetchPriority="high"
                />
              ))}
            </Flex>
          )}
        </Col>
      </Row>
    </Container>
  );
};

const StyledMegaMenuCol = styled(Col)`
  .vertical-mega-menu {
    width: ${MEGA_MENU_COL_WIDTH}px;
    height: ${ABOVE_THE_FOLD_HEIGHT}px;
  }
  .vertical-mega-menu-panel {
    width: calc(75rem - ${MEGA_MENU_COL_WIDTH}px);
    left: ${MEGA_MENU_COL_WIDTH}px;
  }
  .vertical-mega-menu-item {
    height: 28px;
  }
`;

const WideThickWrapper = styled(Shape)`
  .glide__track {
    max-width: 100% !important;
    border-radius: 12px;
  }
  .glide__bullets--override {
    bottom: 1rem !important;
  }
`;
